import { Add } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AttachmentBox from '../../../components/AttachmentBox/AttachmentBox';
import DetailsCard from '../../../components/DetailsCard/DetailsCard';
import FormatCurrency from '../../../components/FormatNumber/FormatCurrency';
import PageTitle from '../../../components/PageTitle/PageTitle';
import TypographyDash from '../../../components/TypographyDash/TypographyDash';
import { dashBorder } from '../../../constants';
import { ProjectStatus } from '../../../enums/projectStatus';
import { useUserRoles } from '../../../hooks/useUserRoles';
import { FileType } from '../../../models/fileType';
import { Project } from '../../../models/project';
import {
  CREATE_INVOICES,
  CREATE_PROJECT,
  EDIT_PROJECT
} from '../../../router/constants';
import { fileService } from '../../../services/fileService';
import { projectService } from '../../../services/projectService';
import CreateInvoiceModal from './CreateInvoiceModal';
import DeclineModal from '../../ProjectsTalent/DeclineModal';
import SuccessModal from '../../../components/Modals/SuccessModal';

const BoldText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.grey['900']
}));

const ProjectDetails: React.FC = () => {
  const projectAttachInputRef = useRef<HTMLInputElement | null>(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isTalent } = useUserRoles();
  const [project, setProject] = useState<Project | undefined>(undefined);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [succesModalOpen, setSuccesModalOpen] = useState<boolean>(false);
  const [declineModalData, setDeclineModalData] = useState<{
    open: boolean;
    project?: Project;
  }>({ open: false });

  useEffect(() => {
    loadProject();
  }, []);

  const loadProject = () => {
    if (id) {
      projectService.getProjectById(Number(id)).then((response) => {
        setProject(response.data);
      });
    }
  };

  const onCreateInvoice = (event: React.MouseEvent<unknown>) => {
    event.stopPropagation();
    setIsOpen(true);
    if (isTalent && project?.status === 'COMPLETED') {
      navigate(`${CREATE_INVOICES}/${project?.id}`);
    }
  };

  const projectAttachmentHandler = () => {
    if (projectAttachInputRef.current) {
      projectAttachInputRef.current.click();
    }
  };

  const onAttachFile = (files: FileList | null) => {
    let filesArray: File[];
    if (files && project?.id) {
      filesArray = Array.from(files);
      const filesFormData = new FormData();
      filesArray.forEach((file) => {
        filesFormData.append('files', file);
      });
      projectService
        .saveProjectFiles(project?.id, filesFormData)
        .then((response) => {
          setProject((prevState) => {
            if (prevState) {
              return {
                ...prevState,
                attachments: [...prevState.attachments, ...response.data]
              };
            }
          });
        });
    }
  };

  const onDeleteAttachment = (fileToRemove: FileType) => {
    fileService.deleteFileById(fileToRemove.uuid).then(() => {
      setProject((prevState) => {
        if (prevState) {
          return {
            ...prevState,
            attachments: prevState?.attachments.filter(
              (item: FileType) => item !== fileToRemove
            )
          };
        }
      });
    });
  };

  const onEditAttachment = (uuid: string, fileName: string) => {
    fileService.editFileName({ uuid, fileName }).then(() => {
      setProject((prevState) => {
        if (prevState) {
          return {
            ...prevState,
            attachments: prevState.attachments.map((attachment) =>
              attachment.uuid === uuid
                ? { ...attachment, fileName }
                : attachment
            )
          };
        }
      });
    });
  };

  const createInvoice = (closeProject?: boolean) => {
    navigate(
      `${CREATE_INVOICES}/${project?.id}/${closeProject ? 'close' : ''}`
    );
  };

  const downloadAttachment = (uuid: string) => {
    fileService.getFileUrl(uuid).then((response) => {
      window.open(response.data, 'blank');
    });
  };

  const acceptProject = (
    event: React.MouseEvent<unknown>,
    project: Project
  ) => {
    event.stopPropagation();
    if (project?.id) {
      projectService.acceptProject(project.id).then(() => {
        loadProject();
        setSuccesModalOpen(true);
      });
    }
  };

  const rejectProject = (
    event: React.MouseEvent<unknown>,
    project: Project
  ) => {
    event.stopPropagation();
    setDeclineModalData({ open: true, project });
  };

  const confirmDeclineProject = (declineReason: string) => {
    if (declineModalData.project?.id) {
      projectService
        .declineProject({
          projectId: declineModalData.project.id,
          declineReason
        })
        .then(() => {
          loadProject();
        });
    }
    setDeclineModalData({ open: false, project: undefined });
  };

  return (
    <>
      <PageTitle>Project Details</PageTitle>
      <DetailsCard
        title={project?.projectTitle ?? ''}
        subtitle={`Project #: ${id}`}
        status={project?.status ? ProjectStatus[project?.status] : undefined}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              ...dashBorder,
              display: 'flex',
              gap: 3,
              flexDirection: { xs: 'column', lg: 'row' }
            }}
          >
            <Box sx={{ flex: '1 1 0px' }}>
              <Typography variant="h6" color="grey.900" mb={2}>
                Project Details
              </Typography>
              <Box display="flex" gap={3}>
                <Stack spacing={1} sx={{ flexShrink: 0 }}>
                  <BoldText>PO #:</BoldText>
                  <BoldText>Category:</BoldText>
                  <BoldText>
                    {isTalent ? `Project created by:` : `Talent:`}
                  </BoldText>
                  <BoldText>Date:</BoldText>
                  <BoldText>Location:</BoldText>
                  <BoldText>Expense Code:</BoldText>
                </Stack>
                <Stack spacing={1} sx={{ flex: '1 0 160px' }}>
                  <TypographyDash value={project?.poNumber} />
                  <TypographyDash value={project?.category} />

                  <Typography whiteSpace="nowrap">
                    {isTalent
                      ? `${project?.projectOwner ?? '--'}`
                      : `${project?.talent?.firstName ?? '--'} ${
                          project?.talent?.lastName ?? ''
                        }`}
                  </Typography>
                  <Typography whiteSpace="nowrap">
                    {project?.startDate
                      ? `${format(
                          new Date(project?.startDate),
                          'MMM do, Y'
                        )} - `
                      : '--'}
                    {project?.endDate
                      ? format(new Date(project?.endDate), 'MMM do, Y')
                      : ''}
                  </Typography>

                  <TypographyDash
                    whiteSpace="nowrap"
                    value={project?.address}
                  />

                  <TypographyDash value={project?.expenseCode} />
                </Stack>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 0px',
                alignItems: { xs: 'start', lg: 'end' }
              }}
            >
              <Box mb={3}>
                <Typography mb={1}>Project Attachments:</Typography>
                {project?.attachments.map((attachment) => (
                  <AttachmentBox
                    key={attachment.id}
                    onClickFile={() => downloadAttachment(attachment.uuid)}
                    fileName={attachment.fileName}
                    onDeleteFile={
                      attachment.owner && isTalent
                        ? () => onDeleteAttachment(attachment)
                        : undefined
                    }
                    onEditFile={
                      attachment.owner && isTalent
                        ? (fileName) =>
                            onEditAttachment(attachment.uuid, fileName)
                        : undefined
                    }
                  />
                ))}
                {!project?.attachments.length && (
                  <AttachmentBox fileName="No Attachments" />
                )}

                {isTalent && (
                  <>
                    <Button
                      size="medium"
                      variant="lightBlue"
                      fullWidth
                      onClick={projectAttachmentHandler}
                      startIcon={<Add />}
                    >
                      Add attachment
                    </Button>
                    <Box sx={{ display: 'none' }}>
                      <input
                        type="file"
                        multiple
                        title="attachment"
                        ref={projectAttachInputRef}
                        onChange={(event) => onAttachFile(event.target.files)}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              gap: 2,
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' }
            }}
          >
            <Box
              sx={{
                ...dashBorder,
                flex: '1 1 0px',
                bgcolor: '#F6F7FB'
              }}
            >
              <Grid container spacing={3}>
                <Grid
                  item
                  container
                  flexDirection="column"
                  spacing={3}
                  xs={12}
                  lg={6}
                >
                  <Grid item xs>
                    <BoldText>Description</BoldText>
                    <Typography>{project?.description}</Typography>
                  </Grid>
                  <Grid item xs>
                    <BoldText>Special Instructions</BoldText>
                    <Typography>{project?.specialInstructions}</Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Grid item xs>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: { sx: 'start', lg: 'end' }
                      }}
                    >
                      <BoldText>Project Budget:</BoldText>
                      {project?.budget ? (
                        <Box>
                          <BoldText fontSize="25px">
                            <FormatCurrency value={project?.budget} />
                          </BoldText>
                          <Typography textAlign="right">Flat fee</Typography>
                        </Box>
                      ) : project?.pricePerHour ? (
                        <>
                          <BoldText fontSize="25px">
                            <FormatCurrency value={project?.pricePerHour} />
                            /hour
                          </BoldText>
                          <Typography>Hourly rate</Typography>
                        </>
                      ) : (
                        <BoldText fontSize="25px">--</BoldText>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs>
                    <Divider sx={{ mt: 1, mb: 2 }} />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: { sx: 'start', lg: 'end' },
                        gap: 2
                      }}
                    >
                      <BoldText>
                        Amount Billed:&nbsp;
                        <span style={{ fontSize: '25px' }}>
                          <FormatCurrency value={project?.amountPaid ?? 0} />
                        </span>
                      </BoldText>

                      <BoldText>
                        Amount Open:&nbsp;
                        <span style={{ fontSize: '25px' }}>
                          <FormatCurrency value={project?.amountOpen ?? 0} />
                        </span>
                      </BoldText>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Stack
            direction="row"
            justifyContent={
              (isAdmin || isTalent) && project && project?.status === 'PENDING'
                ? 'space-between'
                : 'flex-end'
            }
            alignItems="center"
            spacing={2}
            mt={1}
          >
            {isTalent && (
              <Button
                onClick={(e) => {
                  onCreateInvoice(e);
                }}
              >
                Create Invoice
              </Button>
            )}
            {(isAdmin || isTalent) && project && project?.status === 'PENDING' && (
              <Stack direction="row" spacing={2}>
                <Button
                  onClick={(event) => acceptProject(event, project)}
                  variant="outlined"
                  sx={{ width: '150px' }}
                >
                  Accept
                </Button>
                <Button
                  onClick={(event) => rejectProject(event, project)}
                  variant="outlined"
                  sx={{ width: '150px' }}
                >
                  Reject
                </Button>
              </Stack>
            )}
            {!isTalent && (
              <Stack direction="row" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`${CREATE_PROJECT}/${id}`)}
                >
                  Duplicate
                </Button>
                {project && project?.status !== 'COMPLETED' && (
                  <Button
                    sx={{ width: '150px' }}
                    onClick={() => navigate(`${EDIT_PROJECT}/${id}`)}
                  >
                    Edit
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      </DetailsCard>
      {isTalent && (
        <CreateInvoiceModal
          isOpen={isOpen}
          confirmModal={(closeProject) => {
            createInvoice(closeProject);
          }}
          closeModal={() => setIsOpen(false)}
        />
      )}
      <SuccessModal
        open={succesModalOpen}
        handleClose={() => setSuccesModalOpen(false)}
        message="Your project has been approved successfully."
      />
      <DeclineModal
        modalData={declineModalData}
        confirmModal={confirmDeclineProject}
        closeModal={() => setDeclineModalData({ open: false })}
      />
    </>
  );
};

export default ProjectDetails;
